/**
 * Cross-platform SSE hook for real-time work session updates
 *
 * This hook provides a unified interface for subscribing to work session events
 * via Server-Sent Events. It handles:
 * - Automatic reconnection with exponential backoff
 * - Connection state management
 * - Cleanup on unmount
 * - Platform-specific implementations (web vs mobile)
 *
 * Unlike work order SSE which subscribes to a specific work order,
 * work session SSE subscribes to the authenticated user's session events.
 *
 * Connection/reconnect plumbing lives in useSSEChannel; this file only adds
 * the work-session endpoint, event routing, and the work-session error policy
 * (web keeps retrying on every error; mobile stops on 401/403 — see
 * SSEChannelBehavior).
 */
import { useEffect, useRef, useCallback } from 'react';
import { useSSEChannel, parseRawSSEEvent, isReactNative, GenericWebSSEClient, GenericMobileSSEClient, } from './useSSEChannel';
/**
 * Behavior of the work-session SSE clients: quiet lifecycle logging,
 * warn when the mobile client has no auth token, and retry on every
 * error (legacy policy — auth failures are retried too).
 */
const WORK_SESSION_SSE_BEHAVIOR = {
    logPrefix: '[WorkSession SSE]',
    stopOnAuthError: false,
    verboseLogging: false,
    warnOnMissingAuthToken: true,
};
/**
 * Mobile: a 401/403 stops the stream (a rotated token still re-keys and
 * retries — see GenericMobileSSEClient). Retrying a refused bearer token
 * cannot succeed: it was ten more 401s over ~3 minutes in every prod
 * "401 storm". The clock re-subscribes when it renders with a new token.
 */
const MOBILE_WORK_SESSION_SSE_BEHAVIOR = {
    ...WORK_SESSION_SSE_BEHAVIOR,
    stopOnAuthError: true,
};
/**
 * Parse a raw SSE message into a typed work session event
 */
function parseWorkSessionSSEEvent(rawData) {
    return parseRawSSEEvent(rawData, WORK_SESSION_SSE_BEHAVIOR.logPrefix);
}
/**
 * Web-specific SSE client using native EventSource
 * EventSource automatically uses cookies for authentication
 */
class WebWorkSessionSSEClient extends GenericWebSSEClient {
    constructor(options) {
        super(options, WORK_SESSION_SSE_BEHAVIOR);
    }
}
/**
 * Mobile-specific SSE client using fetch-based streaming
 * Uses bearer token authentication via Authorization header
 */
class MobileWorkSessionSSEClient extends GenericMobileSSEClient {
    constructor(options) {
        super(options, MOBILE_WORK_SESSION_SSE_BEHAVIOR);
    }
}
/**
 * Default SSE client factory - creates appropriate client based on platform
 */
function createDefaultWorkSessionSSEClient(options) {
    if (isReactNative()) {
        return new MobileWorkSessionSSEClient(options);
    }
    return new WebWorkSessionSSEClient(options);
}
/**
 * Hook for subscribing to work session SSE events
 *
 * Unlike work order SSE which subscribes to a specific work order,
 * work session SSE subscribes to the authenticated user's session events.
 * The endpoint uses the JWT token to identify the user.
 *
 * @example
 * ```tsx
 * const { isConnected, lastEvent } = useWorkSessionSSE({
 *   baseUrl: 'https://api.example.com/api',
 *   onSessionStarted: (event) => {
 *     console.log('Session started:', event.data.session_id);
 *   },
 *   onSessionEnded: (event) => {
 *     console.log('Session ended:', event.data.session_id);
 *   },
 * });
 * ```
 */
export function useWorkSessionSSE(options) {
    const { baseUrl, authToken, getAuthToken, enabled = true, onEvent, onSessionStarted, onSessionEnded, onSessionUpdated, onBreakStarted, onBreakEnded, onError, onConnectionStateChange, autoReconnect = true, reconnectDelay = 1000, maxReconnectDelay = 30000, maxReconnectAttempts = 10, createClient = createDefaultWorkSessionSSEClient, } = options;
    // Use refs to avoid stale closures in the routing callback
    const callbacksRef = useRef({
        onEvent,
        onSessionStarted,
        onSessionEnded,
        onSessionUpdated,
        onBreakStarted,
        onBreakEnded,
    });
    // Update refs when callbacks change
    useEffect(() => {
        callbacksRef.current = {
            onEvent,
            onSessionStarted,
            onSessionEnded,
            onSessionUpdated,
            onBreakStarted,
            onBreakEnded,
        };
    }, [onEvent, onSessionStarted, onSessionEnded, onSessionUpdated, onBreakStarted, onBreakEnded]);
    // Route incoming events to the appropriate domain callbacks
    const handleEvent = useCallback((event) => {
        callbacksRef.current.onEvent?.(event);
        switch (event.type) {
            case 'work_session_started':
                callbacksRef.current.onSessionStarted?.(event);
                break;
            case 'work_session_ended':
                callbacksRef.current.onSessionEnded?.(event);
                break;
            case 'work_session_updated':
                callbacksRef.current.onSessionUpdated?.(event);
                break;
            case 'break_started':
                callbacksRef.current.onBreakStarted?.(event);
                break;
            case 'break_ended':
                callbacksRef.current.onBreakEnded?.(event);
                break;
        }
    }, []);
    // Work session SSE endpoint uses authenticated user from JWT
    return useSSEChannel({
        url: `${baseUrl}/work-sessions/events`,
        authToken,
        getAuthToken,
        enabled,
        onEvent: handleEvent,
        onError,
        onConnectionStateChange,
        autoReconnect,
        reconnectDelay,
        maxReconnectDelay,
        maxReconnectAttempts,
        createClient,
        hookLabel: '[useWorkSessionSSE]',
    });
}
export { parseWorkSessionSSEEvent, WebWorkSessionSSEClient, MobileWorkSessionSSEClient };
